<app-side-dialog data-testid="ai-connector-dialog" [contentMarginTop]="24">
  <!-- pl-12 offsets the close button (32px + 16px gap) so the header stays centered in the dialog. -->
  <div dialogHeader class="flex flex-col items-center gap-6 w-full pl-12 text-center">
    <div class="flex items-center justify-center gap-3">
      <div class="flex items-center justify-center w-12 h-12 rounded-full bg-shikenso-pink-100">
        <img src="images/logo/logo.png" alt="Shikenso" width="22" />
      </div>
      <span class="text-xl text-shikenso-purple-base-60" aria-hidden="true">⇄</span>
      <app-icon-badge
        content="icons/message-chatbot.svg"
        backgroundColor="var(--shikenso-pink-base-24)"
        color="var(--shikenso-purple-base)"
        [size]="48"
        [iconSize]="24"
        [borderRadius]="100"
      />
    </div>

    <div class="flex flex-col items-center gap-2">
      <h2 class="text-2xl font-medium">{{ 'shared.aiConnector.title' | translate }}</h2>
      <p class="text-sm text-shikenso-purple-base-60">{{ 'shared.aiConnector.subtitle' | translate }}</p>
    </div>
  </div>

  <div dialogContent class="flex flex-col gap-6">
    <div class="flex flex-col gap-3">
      <div class="flex items-center gap-3">
        <app-icon-badge
          content="icons/chat.svg"
          backgroundColor="var(--shikenso-pink-base-24)"
          color="var(--shikenso-purple-base)"
          [size]="36"
          [iconSize]="18"
          [borderRadius]="100"
        />
        <div class="flex flex-col">
          <span class="text-sm font-bold">{{ 'shared.aiConnector.benefits.dataTitle' | translate }}</span>
          <span class="text-xs text-shikenso-purple-base-60">{{
            'shared.aiConnector.benefits.dataText' | translate
          }}</span>
        </div>
      </div>
      <div class="flex items-center gap-3">
        <app-icon-badge
          content="icons/clipboard-list.svg"
          backgroundColor="var(--shikenso-pink-base-24)"
          color="var(--shikenso-purple-base)"
          [size]="36"
          [iconSize]="18"
          [borderRadius]="100"
        />
        <div class="flex flex-col">
          <span class="text-sm font-bold">{{ 'shared.aiConnector.benefits.reportingTitle' | translate }}</span>
          <span class="text-xs text-shikenso-purple-base-60">
            {{ 'shared.aiConnector.benefits.reportingText' | translate }}
          </span>
        </div>
      </div>
    </div>

    <!-- Nav bar + one external panel instead of mat-tab-group: the sections below share one grid
         cell, so the dialog is always as tall as the tallest tab and nothing moves when switching
         tabs (each gray box still hugs its own content via self-start). mat-tab-nav-panel keeps the
         tab/tabpanel ARIA wiring that empty mat-tab bodies would break. -->
    <nav mat-tab-nav-bar class="w-full profile-tab-group" [tabPanel]="tabPanel">
      @for (tab of tabs; track tab) {
        <a mat-tab-link [active]="selectedTab() === tab" (click)="onTabClick(tab)">
          {{ 'shared.aiConnector.tabs.' + tab | translate }}
        </a>
      }
    </nav>

    <mat-tab-nav-panel class="grid" #tabPanel>
      <div
        class="col-start-1 row-start-1 self-start flex flex-col gap-4 p-4 rounded-2xl bg-shikenso-gray-base"
        [class.invisible]="selectedTab() !== EAiConnectorTab.claude"
      >
        <ng-container *ngTemplateOutlet="stepList; context: { $implicit: 'claude' }" />

        <a
          mat-flat-button
          class="w-full"
          target="_blank"
          rel="noopener noreferrer"
          [href]="claudeConnectorUrl"
          (click)="onOpenInClaudeClick()"
        >
          {{ 'shared.aiConnector.claude.openInClaude' | translate }}
        </a>

        <ng-container *ngTemplateOutlet="note; context: { $implicit: 'shared.aiConnector.claude.note' }" />
      </div>

      @if (chatGptEnabled) {
        <div
          class="col-start-1 row-start-1 self-start flex flex-col gap-4 p-4 rounded-2xl bg-shikenso-gray-base"
          [class.invisible]="selectedTab() !== EAiConnectorTab.chatgpt"
        >
          <ng-container *ngTemplateOutlet="stepList; context: { $implicit: 'chatgpt' }" />
          <ng-container *ngTemplateOutlet="note; context: { $implicit: 'shared.aiConnector.chatgpt.note' }" />
        </div>
      }

      <div
        class="col-start-1 row-start-1 self-start flex flex-col gap-4 p-4 rounded-2xl bg-shikenso-gray-base"
        [class.invisible]="selectedTab() !== EAiConnectorTab.other"
      >
        <p class="text-sm">{{ 'shared.aiConnector.other.text' | translate }}</p>

        <div class="flex items-center gap-2 flex-wrap">
          @for (client of ['Cursor', 'VS Code', 'Claude Code', 'Windsurf']; track client) {
            <span
              class="px-3 py-1 rounded-full bg-shikenso-base-white border border-shikenso-purple-base-border text-xs"
            >
              {{ client }}
            </span>
          }
        </div>
      </div>
    </mat-tab-nav-panel>

    <div class="flex items-center gap-2">
      <mat-form-field class="flex-1 custom-form" subscriptSizing="dynamic">
        <mat-label>{{ 'shared.aiConnector.serverUrl' | translate }}</mat-label>
        <input matInput readonly [value]="mcpServerUrl" />
      </mat-form-field>

      <button mat-flat-button color="secondary" type="button" (click)="onCopyServerUrlClick()">
        <div class="flex items-center gap-2">
          <app-icon src="/icons/copy.svg" alt="Copy" [size]="16" />
          {{ 'shared.aiConnector.copy' | translate }}
        </div>
      </button>
    </div>

    <p class="text-xs text-center text-shikenso-purple-base-60">
      {{ 'shared.aiConnector.signInNote' | translate }}
    </p>
    <div class="flex justify-center">
      <button mat-flat-button color="secondary" type="button" mat-dialog-close>
        {{ 'shared.close' | translate }}
      </button>
    </div>
  </div>
</app-side-dialog>

<ng-template #stepList let-tab>
  <ol class="flex flex-col gap-3">
    @for (step of ['step1', 'step2', 'step3']; track step; let index = $index) {
      <li class="flex items-start gap-3 text-sm">
        <span
          class="flex items-center justify-center shrink-0 w-6 h-6 rounded-full bg-shikenso-pink-200 text-xs font-bold"
        >
          {{ index + 1 }}
        </span>
        <span class="pt-0.5">{{ 'shared.aiConnector.' + tab + '.' + step | translate }}</span>
      </li>
    }
  </ol>
</ng-template>

<ng-template #note let-key>
  <div class="flex items-start gap-2 text-xs text-shikenso-purple-base-60">
    <app-icon
      src="icons/info-circle.svg"
      alt="Info"
      class="shrink-0"
      color="var(--shikenso-purple-base-60)"
      [size]="16"
    />
    <span>{{ key | translate }}</span>
  </div>
</ng-template>
